import React, { MouseEvent, useCallback } from 'react';
import { Button } from '@ui/buttons/Button';
import { Icon } from '@ui/Icon';
import { useAddActivityRefMenu } from './AddActivityRefMenuContext';
import { Storey } from '@shared/models/design/storey.model';
import {
  ActivityType,
  programActivityTypes,
} from '@shared/models/design/activities';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';

type AddActivityRefButtonProps = {
  storey: Storey;
  onSelect: (type: ActivityType) => Promise<void> | void;
};

/**
 * Trigger + menu combo for selecting activities that have not yet been assigned to the storey.
 * Mirrors Activities panel UX but keeps trigger colocated with the popover.
 */
export const AddActivityRefButton: React.FC<AddActivityRefButtonProps> = ({
  storey,
  onSelect,
}) => {
  const openActivityRefMenu = useAddActivityRefMenu();
  const isReadonly = useIsReadonly();

  const isDisabled =
    isReadonly ||
    storey.activities.filter(activityRefGuards.is.programActivityRef).length >=
      programActivityTypes.length;

  /**
   * Opens the activity reference menu and applies the chosen activity.
   */
  const handleOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      // Skip opening when there is nothing to add.
      if (isDisabled) return;
      // Use a singleton menu to resolve the selected activity.
      void (async () => {
        const selected = await openActivityRefMenu({
          anchorEl: event.currentTarget,
          storey,
        });
        if (!selected) return;
        await onSelect(selected);
      })();
    },
    [isDisabled, openActivityRefMenu, storey, onSelect],
  );

  return (
    <Button
      variant="text"
      color="neutral"
      startIcon={<Icon category="mui" icon="Add" size="small" />}
      onClick={handleOpen}
      disabled={isDisabled}
    >
      Add program
    </Button>
  );
};
